import React from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  type AccordionProps,
  AccordionSummary,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import { Text } from '@pbt/pbt-ui-components'

const useStyles = makeStyles(
  (theme) => ({
    panel: {
      width: '100%',
    },
    panelExpanded: {
      border: theme.constants.activeFieldBorder,
    },
    panelSummary: {
      padding: theme.spacing(0, 2),
      color: theme.colors.primaryText,
      minHeight: 44,
      '&$panelSummaryExpanded': {
        minHeight: 44,
      },
    },
    panelSummaryExpanded: {
      color: theme.colors.title,
      margin: 'auto 0',
    },
    panelSummaryContent: {
      margin: 0,
      '&&.Mui-expanded': {
        margin: 0,
      },
    },
    panelDetails: {
      padding: theme.spacing(0, 2, 2, 2),
      flexDirection: 'column',
    },
    panelDetailsNoPadding: {
      padding: 0,
    },
    heading: {
      fontSize: '1.6rem',
      fontWeight: 500,
      color: theme.colors.primaryText,
    },
    headingExpanded: {
      color: theme.colors.title,
    },
  }),
  { name: 'PanelAccordion' },
)

const PANEL_ACCORDION_CLASS_NAMES = {
  root: 'PanelAccordion-root',
  summary: 'PanelAccordion-summary',
  details: 'PanelAccordion-details',
  heading: 'PanelAccordion-heading',
  headingExpanded: 'PanelAccordion-headingExpanded',
}

export interface PanelAccordionProps extends Omit<AccordionProps, 'children'> {
  children?: React.ReactNode
  noPadding?: boolean
  onContentClick?: React.MouseEventHandler<HTMLDivElement>
  titleComponent?: React.ReactElement
}

const PanelAccordion = ({
  className,
  expanded,
  title,
  titleComponent,
  children,
  noPadding,
  onContentClick,
  ...rest
}: PanelAccordionProps) => {
  const classes = useStyles()
  const accordionRootClassName = classNames(
    PANEL_ACCORDION_CLASS_NAMES.root,
    className,
    classes.panel,
  )
  const headingClassName = classNames(
    PANEL_ACCORDION_CLASS_NAMES.heading,
    classes.heading,
    {
      [PANEL_ACCORDION_CLASS_NAMES.headingExpanded]: expanded,
      [classes.headingExpanded]: expanded,
    },
  )

  const renderTitleComponent = titleComponent ? (
    React.cloneElement(titleComponent, {
      className: classNames(titleComponent.props.className, headingClassName),
    })
  ) : (
    <Text
      strong
      className={headingClassName}
      variant={expanded ? 'h4' : 'subheading2'}
    >
      {title}
    </Text>
  )

  return (
    <Accordion
      classes={{
        root: accordionRootClassName,
        expanded: classes.panelExpanded,
      }}
      expanded={expanded}
      {...rest}
    >
      <AccordionSummary
        classes={{
          root: classNames(
            PANEL_ACCORDION_CLASS_NAMES.summary,
            classes.panelSummary,
          ),
          expanded: classes.panelSummaryExpanded,
          content: classes.panelSummaryContent,
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        {renderTitleComponent}
      </AccordionSummary>
      <AccordionDetails
        classes={{
          root: classNames(
            PANEL_ACCORDION_CLASS_NAMES.details,
            classes.panelDetails,
            {
              [classes.panelDetailsNoPadding]: noPadding,
            },
          ),
        }}
        onClick={onContentClick}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  )
}

export default PanelAccordion
